import {
  ReportChartCustomizeConfiguration,
  ReportChartGroupCompareItemType,
  ReportsDetailGraphicSchemaValues,
} from '@vision/ui/interfaces';
import { AnySchema, array, boolean, object, string } from 'yup';

export function createFcrValidationParams(
  compareItems: ReportChartGroupCompareItemType[],
  otherwiseSchema?: AnySchema,
  requiresCustomerSchema = false,
) {
  return {
    selectedCompare: array()
      .of(string())
      .when('selectedMeasure', {
        is: 'feedback-fcr',
        then: (schema) =>
          schema.test('fcr-compare-items', 'validation:rules.generic.required', function (value) {
            const expectedCompareItems = [
              ...compareItems,
              ...(requiresCustomerSchema ? (['customer-schema'] as const) : []),
            ];

            return (
              value?.length === expectedCompareItems.length &&
              expectedCompareItems.every((compareItem) => value.includes(compareItem))
            );
          }),
        otherwise: (schema) => otherwiseSchema ?? schema.min(1, 'validation:rules.generic.required'),
      }),
    selectedCustomize: object()
      .shape({
        campaignType: string().nullable(),
        distributionAdjustment: object().shape({
          active: boolean().nullable(),
          filter: string().nullable(),
          operator: string().nullable(),
          value: string().nullable(),
        }),
      })
      .test('fcr-customize-required', 'validation:rules.generic.required', function (value) {
        if (this.parent?.selectedMeasure !== 'feedback-fcr') {
          return true;
        }

        if (!value?.campaignType) {
          return false;
        }

        if (!value?.distributionAdjustment?.active) {
          return true;
        }

        return !!(
          value.distributionAdjustment.filter &&
          value.distributionAdjustment.operator &&
          value.distributionAdjustment.value
        );
      }),
    selectedFilters: object()
      .shape({
        selectedChannelIds: array().of(string()),
      })
      .test('fcr-campaign-required', 'validation:rules.generic.required', function (value) {
        return this.parent?.selectedMeasure !== 'feedback-fcr' || value?.selectedChannelIds?.length === 1;
      }),
    selectedCompareCriteria: object()
      .shape({
        selectedSchemaCodes: array().of(string()),
        selectedQuestionOptionFlowIds: array().of(string()),
        selectedQuestionOptionQuestionIds: array().of(string()),
        selectedQuestionOptionIds: array().of(string()),
      })
      .test('fcr-compare-criteria-required', 'validation:rules.generic.required', function (value) {
        if (this.parent?.selectedMeasure !== 'feedback-fcr') {
          return true;
        }

        return (
          value?.selectedQuestionOptionFlowIds?.length === 1 &&
          value?.selectedQuestionOptionQuestionIds?.length === 1 &&
          value?.selectedQuestionOptionIds?.length > 0 &&
          (!requiresCustomerSchema || value?.selectedSchemaCodes?.length === 1)
        );
      }),
  };
}

export const PieChartFcrValidationParams = createFcrValidationParams(['question-option']);

export const TableChartFcrValidationParams = createFcrValidationParams(
  ['question-option'],
  array()
    .min(2, 'validation:rules.reports.selectedCompare.minTwo')
    .max(2, 'validation:rules.reports.selectedCompare.maxTwo')
    .of(string().required('validation:rules.generic.required'))
    .required('validation:rules.generic.required'),
  true,
);

export const PieChartFcrCustomizeDefaults: ReportChartCustomizeConfiguration = {
  distributionAdjustment: {
    active: false,
    filter: null,
    operator: null,
    value: '',
  },
};

export function hideCompareForFcr(values: ReportsDetailGraphicSchemaValues) {
  return values.selectedMeasure === 'feedback-fcr';
}

export const hideCompareForPieChartFcr = hideCompareForFcr;
export const hideFiltersForFcr = hideCompareForFcr;
